import { IOption } from 'components/select/multiSelect/dropdownSelect/DropdownSelect';
import { useGlobalContext } from 'contexts/GlobalContext';
import { useSimpleTabs } from 'contexts/SimpleTabsContext';
import { useURLParams } from 'hooks/useURLParams';
import { ICycle } from 'models/cycle/cycle.model';
import {
	ISaveSetupParams,
	useGetDefaultSetup,
	useSaveDefaultSetup,
} from 'pages/admin/demand/apportionment/hooks/useDefaultSetup';
import { apportionmentTypeDictionary } from 'pages/admin/demand/apportionment/variables/apportionmentTypeDictionary';
import { useContextPrevision } from 'pages/admin/demand/planning/context/ContextPrevision';
import { PlanningTypes } from 'pages/admin/demand/planning/context/types/contextPrevision.type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCycleList } from 'services/queryClient/wrapperHooks/cycle/useCycleList';
import { useGetCycle } from 'services/queryClient/wrapperHooks/cycle/useGetCycle';

import { usePlanningFilterConfigurationSearch } from '../../../planningLevelConfiguration/components/planningFilterConfiguration/hooks/usePlanningFilterConfigurationSearch';
import { hierarchyFilterByHierarchy } from '../../components/mainHierarchyFilter/context/utils/generateHierarchyFilterBySetup';
import { PLANNING_CONSTANTS } from '../../constants/planningConstants';
import { useApplyFilterPlanning } from '../../hooks/useApplyPlanning';
import { useGetPlanning } from '../../hooks/useGetPlanning';
import { useMultiHierarchyData } from '../../hooks/useMultiHierarchyData';
import { usePlanningPrevisions } from '../../hooks/usePlanningPrevisions';
import { usePlanningStatus } from '../../hooks/usePlanningStatus';
import {
	EmptyPredictionAction,
	useSavePlanning,
} from '../../hooks/useSavePlanning';
import {
	ApportionmentModalState,
	FilterHierarchyApply,
	HierarchyType,
	IFilterHierarchy,
	IPrevisionItem,
	SectionKey,
} from '../../types/planning.types';
import { ICycleOption } from '../../types/planning.types';
import {
	getChildrenLevels,
	HIERARCHY_CONFIGS,
	HIERARCHY_EXCLUSION_MAP,
	IHierarchyConfig,
	IHierarchySelections,
} from '../../variables/PlanningByLevelMappings';

const getManualFillTypeKey = (cycleId: number, planningType: PlanningTypes) =>
	`manualFillType_${cycleId}_${planningType}`;

const getManualFillType = (
	cycleId: number,
	planningType: PlanningTypes
): string | null => {
	try {
		return localStorage.getItem(
			getManualFillTypeKey(cycleId, planningType)
		);
	} catch {
		return null;
	}
};

const saveManualFillType = (
	cycleId: number,
	planningType: PlanningTypes,
	fillType: string
): void => {
	try {
		localStorage.setItem(
			getManualFillTypeKey(cycleId, planningType),
			fillType
		);
	} catch {
		// Silently fail if localStorage is not available
	}
};

const clearManualFillType = (
	cycleId: number,
	planningType: PlanningTypes
): void => {
	try {
		localStorage.removeItem(getManualFillTypeKey(cycleId, planningType));
	} catch {
		// Silently fail if localStorage is not available
	}
};

const usePlanningState = () => {
	const [setup, setSetup] = useState<ISaveSetupParams | undefined>();
	const [applyFilters, setApplyFilters] = useState<FilterHierarchyApply>(
		PLANNING_CONSTANTS.DEFAULTS.FILTERS
	);
	const [
		apportionmentDistributionModalState,
		setApportionmentDistributionModalState,
	] = useState<ApportionmentModalState>(false);
	const [
		isZeroApportionmentDistribution,
		setIsZeroApportionmentDistribution,
	] = useState(false);
	const [hierarchySelections, setHierarchySelections] =
		useState<IHierarchySelections>({});
	const [defaultSetup, setDefaultSetup] = useState<
		ISaveSetupParams | undefined
	>();
	const [manualFillType, setManualFillType] =
		useState<string>('Previsão Manual');
	const [shouldReapplyFill, setShouldReapplyFill] = useState<boolean>(false);
	const [previousCycleId, setPreviousCycleId] = useState<
		number | undefined
	>();

	return {
		setup,
		setSetup,
		applyFilters,
		setApplyFilters,
		apportionmentDistributionModalState,
		setApportionmentDistributionModalState,
		isZeroApportionmentDistribution,
		setIsZeroApportionmentDistribution,
		hierarchySelections,
		setHierarchySelections,
		defaultSetup,
		setDefaultSetup,
		manualFillType,
		setManualFillType,
		shouldReapplyFill,
		setShouldReapplyFill,
		previousCycleId,
		setPreviousCycleId,
	};
};

const useHierarchyConfig = (planningConfig: any) => {
	const getHierarchyConfigs = useCallback((): IHierarchyConfig[] => {
		if (!planningConfig) return [];

		return HIERARCHY_CONFIGS.filter(hierarchy => {
			const hasConfig =
				planningConfig[hierarchy.key as keyof typeof planningConfig];
			const isNotExcluded = !HIERARCHY_EXCLUSION_MAP[
				planningConfig.main_filter.toLowerCase()
			].includes(hierarchy.value);

			return hasConfig && isNotExcluded;
		});
	}, [planningConfig]);

	const availableHierarchies = getHierarchyConfigs().map(h => h.value);
	const mainFilter: HierarchyType = useMemo(() => {
		return planningConfig?.main_filter;
	}, [planningConfig]);
	return {
		getHierarchyConfigs,
		availableHierarchies,
		mainFilter,
	};
};

const useSectionManagement = () => {
	const [showSections, setShowSections] = useState<SectionKey[]>([]);
	const [changeScroll, setChangeScroll] = useState<SectionKey>();

	const onChangeShowSections = useCallback(
		(showSections: SectionKey[], includeLastSection?: SectionKey) => {
			setShowSections(showSections);
			if (includeLastSection) {
				setChangeScroll(includeLastSection);
			}
		},
		[setShowSections]
	);

	useEffect(() => {
		if (changeScroll) {
			const yOffset = -140;
			const element = window.document.getElementById(changeScroll);
			const y =
				(element?.getBoundingClientRect().top || 0) +
				window.pageYOffset +
				yOffset;
			window.scrollTo({ top: y, behavior: 'smooth' });
		}
	}, [changeScroll]);

	return {
		showSections,
		onChangeShowSections,
	};
};

const useSetupOperations = (
	resetAllHierarchiesData: () => void,
	mainFilter: HierarchyType,
	setup: ISaveSetupParams | undefined,
	defaultSetup: ISaveSetupParams | undefined,
	setSetup: React.Dispatch<
		React.SetStateAction<ISaveSetupParams | undefined>
	>,
	apportionmentDistributionModalState: ApportionmentModalState,
	setApportionmentDistributionModalState: React.Dispatch<
		React.SetStateAction<ApportionmentModalState>
	>,
	setIsZeroApportionmentDistribution: React.Dispatch<
		React.SetStateAction<boolean>
	>,
	previsionsDraft: IPrevisionItem[],
	filters: IFilterHierarchy | undefined,
	resetPrevisionsDraft: () => void,
	cycleId: number,
	planningType: PlanningTypes,
	companyAreaId?: number,
	onSuccessCallback?: () => void
) => {
	const [isUpdateMainHierarchyFilterId, setIsUpdateMainHierarchyFilterId] =
		useState<number[] | undefined>();
	const { mutate: savePlanning, isLoading: isSavingPlanning } =
		useSavePlanning(
			() => {
				resetAllHierarchiesData();
				if (onSuccessCallback) {
					onSuccessCallback();
				}
				const idsByHierarchy = {
					product: filters?.productIds || [],
					commercial: filters?.commercialIds || [],
					customer: filters?.customerIds || [],
				};
				const ids = idsByHierarchy[mainFilter];
				setIsUpdateMainHierarchyFilterId(ids);
			},
			() => {
				setIsZeroApportionmentDistribution(true);
			}
		);

	const createPrevision = useCallback(
		(
			setup?: ISaveSetupParams | undefined,
			actionForEmptyPrediction?: EmptyPredictionAction
		) => {
			savePlanning({
				cycleId: cycleId || 0,
				planningType,
				setup,
				actionForEmptyPrediction,
				previsions: previsionsDraft,
				filters,
				companyAreaId,
			});
		},
		[
			savePlanning,
			cycleId,
			planningType,
			previsionsDraft,
			filters,
			companyAreaId,
		]
	);
	const { mutate: saveDefaultSetup } = useSaveDefaultSetup({
		planningType,
		cycleId,
		companyAreaId,
	});
	const handleSave = useCallback(() => {
		if (defaultSetup || setup) {
			createPrevision(setup);
		} else {
			setApportionmentDistributionModalState('save');
		}
	}, [
		defaultSetup,
		setup,
		createPrevision,
		setApportionmentDistributionModalState,
	]);

	const handleSaveDefaultSetup = useCallback(
		(setup: ISaveSetupParams) => {
			if (apportionmentDistributionModalState === 'save') {
				createPrevision(setup);
			} else {
				if (setup.saveAsDefault) {
					saveDefaultSetup(setup);
				} else {
					setSetup(setup);
				}
			}
		},
		[
			apportionmentDistributionModalState,
			createPrevision,
			saveDefaultSetup,
			setSetup,
		]
	);

	const handleZeroApportionmentClose = useCallback(() => {
		setIsZeroApportionmentDistribution(false);
	}, [setIsZeroApportionmentDistribution]);

	const handleOpenApportionment = useCallback(() => {
		setIsZeroApportionmentDistribution(false);
		setApportionmentDistributionModalState('save');
	}, [
		setIsZeroApportionmentDistribution,
		setApportionmentDistributionModalState,
	]);

	const handleSaveEmptyPrediction = useCallback(
		(emptyPredictionAction: EmptyPredictionAction) => {
			createPrevision(setup, emptyPredictionAction);
			setApportionmentDistributionModalState(false);
			setIsZeroApportionmentDistribution(false);
		},
		[
			createPrevision,
			setup,
			setApportionmentDistributionModalState,
			setIsZeroApportionmentDistribution,
		]
	);

	const handleCancel = useCallback(() => {
		resetPrevisionsDraft();
	}, [resetPrevisionsDraft]);

	return {
		handleSave,
		handleSaveDefaultSetup,
		handleZeroApportionmentClose,
		handleOpenApportionment,
		handleSaveEmptyPrediction,
		handleCancel,
		isSavingPlanning,
		isUpdateMainHierarchyFilterId,
		setIsUpdateMainHierarchyFilterId,
	};
};

export const usePlanningByLevel = () => {
	const {
		cycleId: contextCycleId,
		planningType,
		companyAreaId,
		cycle,
		getCanEdit,
	} = useContextPrevision();
	const { userAttributes } = useGlobalContext();
	const { setSelectedCycleId } = useSimpleTabs();

	const [cycleId, setCycleId] = useState<number>(contextCycleId || 0);
	const [syncedCycle, setSyncedCycle] = useState<ICycle | undefined>(cycle);

	const { updateURLParams } = useURLParams();

	const {
		setup,
		setSetup,
		applyFilters,
		setApplyFilters,
		apportionmentDistributionModalState,
		setApportionmentDistributionModalState,
		isZeroApportionmentDistribution,
		setIsZeroApportionmentDistribution,
		hierarchySelections,
		setHierarchySelections,
		defaultSetup,
		setDefaultSetup,
		manualFillType,
		setManualFillType,
		shouldReapplyFill,
		setShouldReapplyFill,
		previousCycleId,
		setPreviousCycleId,
	} = usePlanningState();

	const methods = useForm({
		defaultValues: {
			selectedCycle: cycleId || '',
		},
	});
	const { control, reset } = methods;

	useEffect(() => {
		if (contextCycleId && contextCycleId !== cycleId) {
			setCycleId(contextCycleId);
			reset({
				selectedCycle: contextCycleId,
			});
		}
	}, [contextCycleId, cycleId, reset]);

	const {
		isLoading: isStatusLoading,
		isError: isStatusError,
		canFinalize,
	} = usePlanningStatus({
		planningType,
		cycleId: cycleId || 0,
		companyAreaId,
	});

	const applyFilter = useCallback(
		(
			partialApplyFilters: Partial<FilterHierarchyApply>,
			selectedCycleId?: number
		) => {
			setApplyFilters(prev => ({
				...prev,
				...partialApplyFilters,
			}));
			if (selectedCycleId !== undefined) {
				setCycleId(selectedCycleId);
				setSelectedCycleId(selectedCycleId, 'PLANNING_BY_LEVEL');
				updateURLParams({ 'cycle-id': selectedCycleId });
			}
		},
		[setApplyFilters, setSelectedCycleId, updateURLParams]
	);

	const { data: filter, isLoading: isApplyFiltersLoading } =
		useApplyFilterPlanning(
			{
				cycleId,
				filters: applyFilters,
			},
			planningType
		);

	const { data: fetchedCycle } = useGetCycle(cycleId);
	useEffect(() => {
		if (fetchedCycle) {
			setSyncedCycle(fetchedCycle);
		} else if (cycle) {
			setSyncedCycle(cycle);
		}
	}, [fetchedCycle, cycle]);

	const {
		data: planningData,
		isLoading: planningDataLoading,
		error: planningDataError,
		dataUpdatedAt,
	} = useGetPlanning({
		cycleId: cycleId || 0,
		filters: filter,
		planningType,
		companyAreaId,
	});

	const {
		previsionsDraft,
		setPrevisionsDraft,
		originalPrevisions,
		resetPrevisionsDraft,
		manualPrevisionInput,
		setManualPrevisionInput,
	} = usePlanningPrevisions({
		planningData: planningData || [],
		cycle: syncedCycle,
		manualFillType,
		dataUpdatedAt,
	});

	const { data: planningConfig } = usePlanningFilterConfigurationSearch(
		cycleId,
		userAttributes.email,
		planningType,
		companyAreaId
	);

	const {
		getOptionsForHierarchy,
		getHierarchyLoadingState,
		getHierarchyLoadingMoreState,
		getHierarchyHasNextPage,
		handleHierarchyFocusLoad,
		handleHierarchySearch,
		handleHierarchyLoadMore,
		resetHierarchyLevelData,
		resetAllHierarchiesData,
	} = useMultiHierarchyData({
		cycleId: cycleId || 0,
		hierarchySelections,
		filter,
	});
	const { getHierarchyConfigs, mainFilter } =
		useHierarchyConfig(planningConfig);

	const updateHierarchySelection = useCallback(
		(hierarchyValue: string, selectedItems: IOption[]) => {
			setHierarchySelections(prev => {
				const newSelections: IHierarchySelections = {
					...prev,
					[hierarchyValue]: selectedItems,
				};

				getChildrenLevels(hierarchyValue as any).forEach(childLevel => {
					newSelections[childLevel] = [];
					resetHierarchyLevelData(childLevel as any);
				});
				return newSelections;
			});
		},
		[setHierarchySelections, resetHierarchyLevelData]
	);

	const clearChildrenSelections = useCallback(
		(level: string, selections: IHierarchySelections) => {
			getChildrenLevels(level as any).forEach(childLevel => {
				selections[childLevel] = [];
				resetHierarchyLevelData(childLevel as any);
				clearChildrenSelections(childLevel, selections);
			});
		},
		[resetHierarchyLevelData]
	);

	const updateHierarchySelectionWithCascade = useCallback(
		(hierarchyValue: string, selectedItems: IOption[]) => {
			setHierarchySelections(prev => {
				const newSelections: IHierarchySelections = {
					...prev,
					[hierarchyValue]: selectedItems,
				};
				clearChildrenSelections(hierarchyValue, newSelections);
				return newSelections;
			});
		},
		[setHierarchySelections, clearChildrenSelections]
	);

	useEffect(() => {
		if (!planningConfig?.main_filter) return;

		const mainFilterLevel = planningConfig.main_filter;
		const hierarchyFilter = hierarchyFilterByHierarchy[mainFilterLevel];

		Object.keys(hierarchySelections).forEach(level => {
			if (level === mainFilterLevel) return;

			const levelConfig = hierarchyFilter[level as any];
			if (
				levelConfig?.father &&
				hierarchySelections[levelConfig.father]?.length > 0
			) {
				if (hierarchySelections[level]?.length === 0) {
					handleHierarchyFocusLoad(level);
				}
			}
		});
	}, [hierarchySelections, handleHierarchyFocusLoad, planningConfig]);

	const { data: cyclesData } = useCycleList();

	const cycleOptions: ICycleOption[] =
		cyclesData
			?.sort((a, b) => {
				const dateA = new Date(a.createdAt || 0).getTime();
				const dateB = new Date(b.createdAt || 0).getTime();
				return dateB - dateA;
			})
			?.map((cycle: ICycle) => ({
				id: cycle.id,
				value: cycle.id,
				label:
					cycle.ciclo && cycle.ano
						? `${String(cycle.ciclo).padStart(2, '0')}/${cycle.ano}`
						: `Ciclo ${cycle.id}`,
			})) || [];

	const paramsDefaultSetup = {
		planningType,
		cycleId,
		companyAreaId,
	};

	const { data: defaultSetupResult, isLoading: loadingDefaultSetup } =
		useGetDefaultSetup(paramsDefaultSetup);

	useEffect(() => {
		setDefaultSetup(
			defaultSetupResult
				? {
						saveAsDefault: true,
						selectedBase: defaultSetupResult.base_type,
						endMonth:
							defaultSetupResult.sales_based_apportionment_setup
								?.sale_end_date,
						startMonth:
							defaultSetupResult.sales_based_apportionment_setup
								?.sale_start_date,
						selectedCycleId: defaultSetupResult?.based_cycle_id,
						selectedCycleLabel:
							defaultSetupResult?.based_cycle?.month_year,
				  }
				: undefined
		);
	}, [defaultSetupResult, setDefaultSetup]);

	useEffect(() => {
		if (cycleId && planningType) {
			const savedType = getManualFillType(cycleId, planningType);
			if (savedType && savedType !== manualFillType) {
				setManualFillType(savedType);
			}
		}
	}, [cycleId, planningType]);

	useEffect(() => {
		if (cycleId && planningType && manualFillType) {
			saveManualFillType(cycleId, planningType, manualFillType);
		}
	}, [cycleId, planningType, manualFillType]);

	useEffect(() => {
		if (cycleId !== previousCycleId && previousCycleId !== undefined) {
			if (planningType && previousCycleId) {
				clearManualFillType(previousCycleId, planningType);
			}
			setManualFillType('Previsão Manual');
		}
		setPreviousCycleId(cycleId);
	}, [cycleId, previousCycleId, planningType]);

	useEffect(() => {
		const hasSelections = Object.keys(hierarchySelections).length > 0;
		if (hasSelections) {
			const timer = setTimeout(() => {
				setShouldReapplyFill(true);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [hierarchySelections]);

	useEffect(() => {
		const hasFilters =
			JSON.stringify(applyFilters) !==
			JSON.stringify(PLANNING_CONSTANTS.DEFAULTS.FILTERS);
		if (hasFilters) {
			const timer = setTimeout(() => {
				setShouldReapplyFill(true);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [applyFilters]);

	const { onChangeShowSections, showSections } = useSectionManagement();

	const {
		handleSave,
		handleSaveDefaultSetup,
		handleZeroApportionmentClose,
		handleOpenApportionment,
		handleSaveEmptyPrediction,
		handleCancel,
		isSavingPlanning,
		isUpdateMainHierarchyFilterId,
		setIsUpdateMainHierarchyFilterId,
	} = useSetupOperations(
		resetAllHierarchiesData,
		mainFilter,
		setup,
		defaultSetup,
		setSetup,
		apportionmentDistributionModalState,
		setApportionmentDistributionModalState,
		setIsZeroApportionmentDistribution,
		previsionsDraft,
		filter,
		resetPrevisionsDraft,
		cycleId || 0,
		planningType,
		companyAreaId,
		() => {
			setManualFillType('Previsão Manual');
		}
	);
	const apportionmentTypeText =
		apportionmentTypeDictionary[
			setup?.selectedBase || defaultSetup?.selectedBase || ''
		] || 'não definida';
	const canEdit = getCanEdit(syncedCycle);

	return {
		setup,
		defaultSetup,
		apportionmentDistributionModalState,
		isZeroApportionmentDistribution,
		hierarchySelections,
		previsionsDraft,
		originalPrevisions,
		syncedCycle,
		canEdit,

		mainFilter,
		filters: filter,
		cycle,
		companyAreaId,
		planningDataLoading,
		planningDataError,
		showSections,

		isStatusLoading,
		isStatusError,
		canFinalize,

		control,
		getValues: methods.getValues,
		cycleOptions,
		cycleId,
		planningType,
		isApplyFiltersLoading,
		isSavingPlanning,

		setPrevisionsDraft,
		handleSave,
		handleSaveDefaultSetup,
		handleZeroApportionmentClose,
		handleOpenApportionment,
		handleSaveEmptyPrediction,
		handleCancel,
		setApportionmentDistributionModalState,
		setHierarchySelections,
		onChangeShowSections,

		getHierarchyConfigs,
		updateHierarchySelection,
		updateHierarchySelectionWithCascade,
		getOptionsForHierarchy,
		getHierarchyLoadingState,
		getHierarchyLoadingMoreState,
		getHierarchyHasNextPage,
		handleHierarchyFocusLoad,
		handleHierarchySearch,
		handleHierarchyLoadMore,
		applyFilter,
		resetAllHierarchiesData,

		loadingDefaultSetup,
		apportionmentTypeText,
		reset: control._reset,
		manualPrevisionInput,
		setManualPrevisionInput,
		manualFillType,
		setManualFillType,
		shouldReapplyFill,
		setShouldReapplyFill,
		isUpdateMainHierarchyFilterId,
		setIsUpdateMainHierarchyFilterId,
	};
};
